import {
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { FC, useEffect, useState } from 'react';
import { isNullOrUndefined } from 'util';
import { useLocaleConfig } from '../../stores/LocaleConfigProvider';
import { DSItem, DSOrder, StatusBoardType } from './StatusTypeList.stories';

const useStyles = makeStyles(theme => ({
  card: {
    minWidth: 280,
    maxWidth: 280,
    margin: 'auto',
    background: 'lightgray'
  },
  cardBig: {
    minWidth: 500,
    maxWidth: 200,
    margin: 'auto',
    background: 'lightgray'
  },
  name: {
    textAlign: 'left',
    margin: 5,
    lineHeight: 1.2,
    alignSelf: 'left'
  },
  cardActions: {
    justifyContent: 'left',
    margin: '0px 5px 0px 0px',
    padding: '0px',
    flexGrow: 1,
    borderBottom: '10',
    borderColor: 'black'
  },
  nameFull: {
    textAlign: 'left',
    flexGrow: 1,
    margin: 5,
    lineHeight: 1.2,
    justifyContent: 'left',
    alignSelf: 'flex-start'
  },
  option: {
    textAlign: 'left',
    margin: 5,
    marginLeft: 15,
    lineHeight: 1.2,
    justifyContent: 'left',
    alignSelf: 'flex-start'
  },
  nameFullReady: {
    textAlign: 'left',
    flexGrow: 1,
    margin: 5,
    lineHeight: 1.2,
    justifyContent: 'left',
    alignSelf: 'flex-start',
    textDecoration: 'line-through'
  },
  optionReady: {
    textAlign: 'left',
    margin: 5,
    marginLeft: 15,
    lineHeight: 1.2,
    justifyContent: 'left',
    alignSelf: 'flex-start',
    textDecoration: 'line-through'
  },
  orderInfo: {
    background: 'green',
    color: 'white'
  },
  orderInfoGrey: {
    background: 'grey',
    color: 'white'
  },
  itemgrid: {
    flexDirection: 'column'
  },
  itemNameGrid: {
    flexDirection: 'row'
  },
  itemButton: {
    width: '90%'
  },
  selectedItemButton: {
    width: '90%',
    background: 'orange',
    animationName: '$blinker',
    animationDuration: '1s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite'
  },
  '@keyframes blinker': {
    from: { opacity: 1 },
    to: { opacity: 0 }
  },
  checkButton: {
    width: '20px'
  },
  checkButtonBig: {
    width: '30px'
  }
}));

interface OrderCardProps {
  statusType: StatusBoardType;
  order: DSOrder;
  fullView?: boolean;
  onStatusChanged?: (order: DSOrder) => void;
  onShowOrderView?: (order: DSOrder) => void;
}

const OrderCard: FC<OrderCardProps> = ({
  statusType,
  order,
  fullView,
  onStatusChanged,
  onShowOrderView
}: OrderCardProps) => {
  const classes = useStyles();
  const localeConfig = useLocaleConfig();

  let orderTime = order.area_name;
  let orderIds = order.id.split('_');
  if (orderIds.length > 1) {
    orderTime = moment(orderIds[1], 'YYMMDDHHmmssSS').format('HH:mm');
  } else {
    orderTime = moment(order.id, 'YYMMDDHHmmssSS').format('HH:mm');
  }
  let initialItems = order.items;
  const [state, setState] = useState({
    order: order,
    items: initialItems,
    ready:
      initialItems.findIndex(i => !(i.kitchen_ready || i.status === 'void')) < 0
  });

  useEffect(() => {
    let items = order.items;
    let ready =
      items.findIndex(i => !(i.kitchen_ready || i.status === 'void')) < 0;
    setState({
      ...state,
      order: order,
      items: items,
      ready: ready
    });
  }, [order]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectItem = (item: DSItem) => {
    let items = state.items;
    let existingItem = items.find(i => i.id === item.id);
    if (existingItem) {
      if (statusType.id === '1' && !existingItem.kitchen_checked) {
        existingItem.kitchen_ready = !item.kitchen_ready;
        let ready =
          items.findIndex(t => !(t.kitchen_ready || t.status === 'void')) < 0;
        setState({
          ...state,
          items: items,
          ready: ready
        });
      } else if (statusType.id === '2') {
        existingItem.kitchen_checked = !existingItem.kitchen_checked;
        setState({
          ...state,
          items: items
        });
      }
    }
    if (onStatusChanged) onStatusChanged(order);
  };

  const handleShowOrderView = () => {
    if (onShowOrderView) {
      onShowOrderView(order);
    }
  };

  return (
    <Card className={fullView ? classes.cardBig : classes.card}>
      <CardActionArea
        onClick={() => handleShowOrderView()}
        className={
          state.items.findIndex(i => !i.kitchen_checked) >= 0
            ? classes.orderInfo
            : classes.orderInfoGrey
        }
      >
        <Grid container key="order_area">
          <Typography
            variant={fullView ? 'h5' : 'h6'}
            className={classes.nameFull}
          >
            {orderTime}
          </Typography>
          <Typography
            variant={fullView ? 'h5' : 'h6'}
            className={classes.name}
            align="right"
          >
            {order.customer_name}
          </Typography>
        </Grid>
        <Grid container key="order_table">
          <Typography
            variant={fullView ? 'h5' : 'h6'}
            className={classes.nameFull}
          >
            {isEmpty(order.table_name) ? order.area_name : order.table_name}
          </Typography>
          <Typography
            variant={fullView ? 'h5' : 'h6'}
            className={classes.name}
            align="right"
          >
            {'#' +
              order.order_no +
              '-Gst.' +
              (isNullOrUndefined(order.persons) ? 0 : order.persons)}
          </Typography>
        </Grid>
      </CardActionArea>
      <CardContent>
        <Grid container spacing={2} justify="center" key="item_content">
          {state.items.map(item => (
            <Grid container key={item.id}>
              <CardActions className={classes.cardActions}>
                <Grid
                  container
                  key={'itemName' + item.id}
                  className={classes.itemNameGrid}
                >
                  <Button
                    onDoubleClick={() => handleSelectItem(item)}
                    size="small"
                    color="primary"
                    className={
                      item.kitchen_selected
                        ? classes.selectedItemButton
                        : classes.itemButton
                    }
                    disabled={
                      statusType.id === '3' ||
                      (statusType.id === '2' && !item.kitchen_ready)
                    }
                  >
                    <Grid
                      container
                      key={'item' + item.id}
                      className={classes.itemgrid}
                    >
                      <Typography
                        variant={fullView ? 'h6' : 'subtitle1'}
                        color="textPrimary"
                        className={
                          item.kitchen_ready || item.status === 'void'
                            ? classes.nameFullReady
                            : classes.nameFull
                        }
                        gutterBottom
                      >
                        {item.count +
                          ' x ' +
                          item.name +
                          (item.status === 'void' ? '(Cancelled)' : '')}
                      </Typography>

                      {item.modifiers &&
                        item.modifiers.length > 0 &&
                        item.modifiers.map(
                          mod =>
                            mod.options &&
                            mod.options.length > 0 &&
                            mod.options.map(opt => (
                              <Grid
                                container
                                key={opt.id}
                                className={classes.itemgrid}
                              >
                                <Typography
                                  className={
                                    item.kitchen_ready
                                      ? classes.optionReady
                                      : classes.option
                                  }
                                >
                                  {'>> ' + mod.prefix + ' ' + opt.name}
                                </Typography>
                                {opt.suboptions &&
                                  opt.suboptions.map(sub => (
                                    <Typography
                                      className={
                                        item.kitchen_ready
                                          ? classes.optionReady
                                          : classes.option
                                      }
                                      key={opt.id + '_' + sub.id}
                                    >
                                      {'>> ' + sub.name}
                                    </Typography>
                                  ))}
                              </Grid>
                            ))
                        )}
                      {!isEmpty(item.note) && (
                        <Typography
                          className={
                            item.kitchen_ready
                              ? classes.optionReady
                              : classes.option
                          }
                        >
                          {'// ' +
                            item.note +
                            ' ' +
                            localeConfig.formatCurrency(
                              item.price_note ? item.price_note : 0
                            )}
                        </Typography>
                      )}
                    </Grid>
                  </Button>

                  {item.kitchen_checked &&
                    (statusType.id === '1' ||
                      statusType.id === '2' ||
                      statusType.id === '3') && (
                      <IconButton
                        className={
                          fullView
                            ? classes.checkButtonBig
                            : classes.checkButton
                        }
                      >
                        <CheckCircle />
                      </IconButton>
                    )}
                </Grid>
              </CardActions>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default OrderCard;
